import { useAuth0, User } from '@auth0/auth0-react';
import axios from 'axios';
import CircleLoader from 'components/common/CircleLoader';
import ErrorDisplay from 'components/common/ErrorDisplay';
import Icon from 'components/common/Icon';
import Button from 'components/common/forms/Button';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getEnvVar } from 'util/env';
import { Role, UserV2 } from '../types/domainModels';
import { identifyUser } from '../util/analytics';
import { getItem, LOCALSTORAGE_KEYS, setItem } from '../util/localStorage';
import { Auth0JwtPayload } from 'types/auth0JwtPayload';
import { getAPIUrl } from 'services/backend/baseAPI';
import { setTokenRefresher } from 'apiRequests';
import {
  identifyAppcuesUser,
  identifyAppcuesGroup,
  resetAppcuesUser,
  isAppcuesAvailable,
} from 'services/appcues/appcues';

export interface AuthData {
  id: number;
  email: string;
  organizationId: number;
  role: Role;
  userMetadata: Record<string, unknown>;
}

interface Auth {
  data: AuthData | null;
  isLoggedIn: boolean;
  user?: UserV2;
  logout: () => void;
  changeOrganization: (organizationId: number) => Promise<void>;
}

type Auth0User = Pick<User, 'email' | 'given_name' | 'family_name'> &
  Pick<
    Auth0JwtPayload,
    'https://useglass.com/user_metadata' | 'https://useglass.com/org_name'
  >;

export const AuthContext = createContext<Auth | undefined>(undefined);

const UNAUTHORIZED_EVENT_NAME = 'glass:unauthorized';

export function dispatchUnauthorizedEvent() {
  const unauthorizedEvent = new Event(UNAUTHORIZED_EVENT_NAME);
  document.dispatchEvent(unauthorizedEvent);
}

const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const {
    user: userFromAuth0,
    error,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout: logoutAuth0,
  } = useAuth0<Auth0User>();

  const [data, setData] = useState<Auth['data'] | null>(null);
  const [jwt, setJwt] = useState(() => getItem(LOCALSTORAGE_KEYS.TOKEN));
  const [user, setUser] = useState<UserV2 | undefined>(undefined);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    // Lets the axios request interceptor silently get a fresh access token
    // once the current one is at (or near) its 24 hour expiry, instead of
    // relying on the token fetched once below, which can go stale when idle.
    setTokenRefresher(async () => {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${getEnvVar('AUTH0_AUDIENCE')}/api/v2/`,
          scope: 'read:current_user',
        },
      });

      setJwt(accessToken);

      return accessToken;
    });

    return () => setTokenRefresher(null);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!userFromAuth0 || data) {
      return;
    }

    const getUserMetadata = async () => {
      setIsInitializing(true);

      try {
        const organizationId = userFromAuth0['https://useglass.com/org_name'];

        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: `https://${getEnvVar('AUTH0_AUDIENCE')}/api/v2/`,
            scope: 'read:current_user',
          },
        });

        setItem(LOCALSTORAGE_KEYS.TOKEN, accessToken);
        setJwt(accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        const userSessionData: UserV2 = {
          email: userFromAuth0.email ?? '',
          firstName: userFromAuth0.given_name ?? '',
          lastName: userFromAuth0.family_name ?? '',
          hashFront: userFromAuth0.email ?? '',
          id: userFromAuth0['https://useglass.com/user_metadata'].id,
          image: {},
          mobile:
            userFromAuth0['https://useglass.com/user_metadata']?.mobile ?? '',
        };

        setUser(userSessionData);
        identifyUser({ organizationId, user: userSessionData });

        // Identify user and organization to Appcues for onboarding flows
        if (isAppcuesAvailable()) {
          identifyAppcuesUser(userSessionData.id.toString(), {
            email: userSessionData.email,
            firstName: userSessionData.firstName,
            lastName: userSessionData.lastName,
            role: userFromAuth0['https://useglass.com/user_metadata'].role,
            organizationId,
          });

          identifyAppcuesGroup(organizationId.toString(), {
            organizationId,
          });
        }

        setItem(LOCALSTORAGE_KEYS.CURRENT_ORGANIZATION_ID, organizationId);

        setData({
          id: userFromAuth0['https://useglass.com/user_metadata'].id,
          email: userFromAuth0.email ?? '',
          organizationId,
          role: userFromAuth0['https://useglass.com/user_metadata'].role,
          userMetadata:
            userFromAuth0['https://useglass.com/user_metadata'] || {},
        });
      } finally {
        setIsInitializing(false);
      }
    };

    getUserMetadata();
  }, [userFromAuth0, getAccessTokenSilently, data]);

  const logout: Auth['logout'] = () => {
    // Reset Appcues user identification on logout
    if (isAppcuesAvailable()) {
      resetAppcuesUser();
    }

    setItem(LOCALSTORAGE_KEYS.TOKEN, null);
    logoutAuth0({ logoutParams: { returnTo: window.location.origin } });
  };

  const changeOrganization: Auth['changeOrganization'] = async (
    organizationId: number,
  ) => {
    if (data?.organizationId === organizationId) {
      return;
    }
    await axios.post(
      getAPIUrl({ path: '/users/change-organization', version: 'v1' }),
      {
        organizationId,
      },
    );

    setItem(LOCALSTORAGE_KEYS.CURRENT_ORGANIZATION_ID, organizationId);
    setData((prev) => (prev ? { ...prev, organizationId } : prev));
  };

  if (error) {
    return (
      <ErrorDisplay message={error.message}>
        <Button
          hierarchy="secondary-gray"
          icon={<Icon id="log-out-01" />}
          iconPlacement="leading"
          onClick={() => logout()}
          size="sm"
          type="button"
        >
          Logout
        </Button>
      </ErrorDisplay>
    );
  }

  if (isLoading || isInitializing) {
    return <CircleLoader isColored={true} />;
  }

  if (!isAuthenticated) {
    const queryParams = new URLSearchParams(window.location.search);

    loginWithRedirect({
      authorizationParams: {
        invitation: queryParams.get('invitation') ?? undefined,
        organization: queryParams.get('organization') ?? undefined,
        prompt: 'login',
      },
      appState: {
        returnTo: '/dashboard',
      },
    });

    return <div>Authenticating ...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        data,
        isLoggedIn: isAuthenticated && !!jwt,
        user,
        logout,
        changeOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used in an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
